/**
 * /pipeline-facility/ai — the AI Drafts register (batch 6, fieldiq-api#1236).
 *
 * A full-width register of the company's stored AI runs (GET /ai/digests,
 * newest first, the API's latest 20), filtered by kind and by job in the URL
 * so a draft's back arrow returns to the list as it was left. A row opens the
 * draft on its own page.
 *
 * The band runs the compliance digest (as of the tenant's today); daily
 * summaries are run from a job's Daily logs tab and preflights from a ticket
 * or a job's Tickets tab, and all of them are listed here.
 *
 * A pending draft reads "Awaiting your approval" — it is not published until a
 * person approves it. Admin, office staff and supervisor only (PF_AI_ROLES);
 * anyone else gets a plain sentence and no request is made.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardCheck, FileClock, Loader2, Sparkles } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { useAuth } from '../../../contexts/AuthContext';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { usePfAiDigests, useRunPfAi } from '../../../hooks/usePfAi';
import { usePfJobs } from '../../../hooks/usePipelineFacility';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { formatInstant } from '../../../lib/calendar-date';
import { queryLoadFailure } from '../../../lib/load-failure';
import { PF_AI_AGENT_LABEL, canUsePfAi, pfAiAgentLabel, pfAiOutcomeSkin, pfAiRefusal, pfAiScopeText } from '../../../lib/pf-ai';
import { pfDay } from '../../../lib/pipeline-facility';
import { PF_AI_AGENTS, type PfAiStoredRun } from '../../../types/pf-ai';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useOpenRecord } from '../../downhole/recordPage';
import { PF_EYEBROW, PF_INPUT, PfBadge, PfChip, PfChipRow, PfEmpty, PfField, PfFormError, PfPanel, PfRegister, type PfColumn } from '../PfBits';

type Row = PfAiStoredRun & { id: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function JobPick({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const jobs = usePfJobs();
  const list = jobs.data?.data ?? [];
  return (
    <div className="w-full sm:w-72">
      <PfField label="Job">
        <select className={PF_INPUT} aria-label="Job" value={value} onChange={(e) => onChange(e.target.value)} data-testid="pf-ai-job-filter">
          <option value="">All jobs</option>
          {list.map((j) => (
            <option key={j.id} value={j.id}>
              {j.jobNumber}
              {j.customerName ? ` · ${j.customerName}` : ''}
            </option>
          ))}
          {value && !list.some((j) => j.id === value) && <option value={value}>This job</option>}
        </select>
      </PfField>
    </div>
  );
}

export default function PfAiDraftsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const allowed = canUsePfAi(user?.role);
  const tz = useTenantTimeZone();
  const [params, setParams] = useSearchParams();
  const agentParam = params.get('agent') ?? '';
  const agent = (PF_AI_AGENTS as readonly string[]).includes(agentParam) ? agentParam : '';
  const jobParam = params.get('jobId') ?? '';
  const jobId = UUID.test(jobParam) ? jobParam : '';
  const listQuery = usePfAiDigests({ agent: agent || undefined, jobId: jobId || undefined }, allowed);
  const allQuery = usePfAiDigests({}, allowed);
  const run = useRunPfAi();
  const [runError, setRunError] = useState<string | null>(null);

  const rows: Row[] = (listQuery.data ?? []).map((d) => ({ ...d, id: d.agentRunId }));
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data;
  const pendingCount = all ? all.filter((d) => d.outcome === 'pending').length : undefined;

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const opened = (id: string | undefined) => {
    if (id) openRecord(`/pipeline-facility/ai/${id}`);
  };

  const runCompliance = () => {
    setRunError(null);
    run.mutate({ agent: 'pf_compliance_digest' }, {
      onSuccess: (r) => opened(r?.agentRunId),
      onError: (err) => setRunError(pfAiRefusal(err, 'The compliance digest did not run.')),
    });
  };

  const columns: PfColumn<Row>[] = [
    { key: 'kind', header: 'Draft', render: (d) => <span className="font-extrabold text-white">{pfAiAgentLabel(d.agent)}</span> },
    { key: 'covers', header: 'Covers', render: (d) => pfAiScopeText(String(d.agent), d.triggerData, pfDay) },
    { key: 'run', header: 'Run', render: (d) => formatInstant(d.createdAt, tz) ?? '—' },
    { key: 'draft', header: 'AI text', render: (d) => (d.draft ? 'Drafted' : 'Unavailable') },
    { key: 'status', header: 'Review', render: (d) => <PfBadge skin={pfAiOutcomeSkin(d.outcome)} value={d.outcome} /> },
  ];

  const actions = allowed ? (
    <div className="flex flex-wrap gap-2" data-testid="pf-ai-run-actions">
      <button type="button" className={DH_BAND_SECONDARY} onClick={runCompliance} disabled={run.isPending} data-testid="pf-ai-run-compliance">
        {run.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <ClipboardCheck className="size-4" aria-hidden="true" />}
        {run.isPending ? 'Running…' : 'Run compliance digest'}
      </button>
    </div>
  ) : undefined;

  const emptyText = agent
    ? `No ${PF_AI_AGENT_LABEL[agent as keyof typeof PF_AI_AGENT_LABEL].toLowerCase()} has been run${jobId ? ' for this job' : ''}.`
    : jobId
      ? 'No AI draft has been run for this job.'
      : 'No AI draft has been run yet. Run one from a job, a ticket, or the compliance digest above.';

  return (
    <div data-testid="pf-ai-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title="AI Drafts"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={actions}
        stats={
          allowed
            ? [
                { icon: <FileClock className="size-4" aria-hidden="true" />, label: 'To review', sub: 'Awaiting approval', tone: 'accent', loading: allQuery.isLoading, value: displayValue(pendingCount) },
                { icon: <Sparkles className="size-4" aria-hidden="true" />, label: 'Drafts', sub: 'Latest 20', tone: 'neutral', loading: allQuery.isLoading, value: displayValue(all ? all.length : undefined) },
              ]
            : undefined
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!allowed ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="pf-ai-not-allowed">
              AI drafts are for admins, office staff and supervisors.
            </p>
          ) : (
            <>
              <p className="text-xs text-fieldiq-muted">
                Figures are computed exactly. The AI writes a draft explanation that waits for a person to approve or dismiss it — nothing is sent or published by the AI.
              </p>
              {runError && <PfFormError message={runError} />}
              <PfChipRow label="Draft kind">
                <PfChip label="All" active={!agent} onClick={() => setParam('agent', '')} />
                {PF_AI_AGENTS.map((a) => (
                  <PfChip key={a} label={PF_AI_AGENT_LABEL[a]} active={agent === a} onClick={() => setParam('agent', a)} />
                ))}
              </PfChipRow>
              <JobPick value={jobId} onChange={(v) => setParam('jobId', v)} />
              <PfPanel eyebrow="AI drafts" title="Drafts" testId="pf-ai-register">
                <PfRegister
                  query={listQuery}
                  rows={rows}
                  columns={columns}
                  rail={(d) => pfAiOutcomeSkin(d.outcome).rail}
                  onOpen={(d) => opened(d.agentRunId)}
                  subject="AI drafts"
                  testId="pf-ai-run"
                  minWidth="min-w-[760px]"
                  empty={<PfEmpty>{emptyText}</PfEmpty>}
                  card={(d) => (
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-extrabold text-white">{pfAiAgentLabel(d.agent)}</p>
                        <p className="mt-0.5 text-xs text-fieldiq-muted">{pfAiScopeText(String(d.agent), d.triggerData, pfDay)}</p>
                        <p className="mt-0.5 text-xs text-fieldiq-muted">{formatInstant(d.createdAt, tz) ?? '—'}</p>
                      </div>
                      <PfBadge skin={pfAiOutcomeSkin(d.outcome)} value={d.outcome} />
                    </div>
                  )}
                />
              </PfPanel>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
